export type UseCase =
  | "Umschreiben"
  | "Grammatik checken"
  | "Professionalisieren"
  | "Eigene Anweisungen";
export const useCaseOptions: UseCase[] = [
  "Umschreiben",
  "Grammatik checken",
  "Professionalisieren",
  "Eigene Anweisungen",
];

export const caseTexts: Record<UseCase, { header: string; button: string; pending: string }> = {
  Umschreiben: {
    header: "Email umschreiben",
    button: "Email umschreiben",
    pending: "Email wird umgeschrieben...",
  },
  "Grammatik checken": {
    header: "Email korrigieren",
    button: "Email korrigieren",
    pending: "Email wird korrigiert...",
  },
  Professionalisieren: {
    header: "Email professioneller machen",
    button: "Email professionalisieren",
    pending: "Email wird professioneller gemacht...",
  },
  "Eigene Anweisungen": {
    header: "Antwort erstellen",
    button: "Antwort vorschlagen",
    pending: "Antwort wird generiert...",
  },
};

export const actions: Record<UseCase, string> = {
  Umschreiben: `
    You are an expert professional email writer. Your task is to take the text provided by the user and rewrite it as a genuinely different, alternative version — not a minor polish of the same phrasing.

    IMPORTANT You must:
    - Preserve the intent and meaning of the original text exactly.
    - Produce a NOTICEABLY DIFFERENT formulation: vary sentence structure, word choice, sentence order, and paragraph flow compared to the input.
    - This applies even if the input text is already well-written, correct, and professional — your job is not to judge whether it's "good enough", but to offer a genuine alternative version the user can compare against their own.
    - Do NOT return text that is nearly identical or only marginally reworded compared to the input — that is a failure to complete the task, regardless of how polished the input already is.
    - Improve clarity and readability without losing any important information.
    - Enhance tone and style to make it sound natural, polite, and professional, suitable for business or personal contexts.
    - Fix grammar, spelling, and punctuation errors.
    - Organize content logically (paragraphs, sentences, formatting) to make it easy to read.
    - Keep it concise but do not remove essential details.

    Output only the rewritten text. Do not add explanations or extra notes. Format it as a polished email, ready to send.
    `,

  "Grammatik checken": `
     You are an expert proofreader. Your task is to take the text provided by the user and correct all grammar, spelling, and punctuation errors. You must:
     
     IMPORTANT You must:
     - Leave the wording, tone, and style of the original text unchanged.
     - Only fix mistakes; do not rephrase, reorganize, or add content.
     - Preserve the meaning and intent of the text exactly.
     - Output the corrected text clearly, ready for the user to use.
    
     Do not add explanations, comments, or suggestions. Only return the corrected text.
    `,

  Professionalisieren: `
    You are an expert professional email and business writer. Your task is to take the text provided by the user, which may contain casual language or rough ideas, and rewrite it into a highly professional, clear, and polished version. You must:
    
    IMPORTANT You must:
    - Turn informal or incomplete ideas into well-structured, professional sentences.
    - Greeting and closing should be formal as well
    - Ensure the text is suitable for business communication or high-level recipients.
    - Preserve the core message and intent of the user.
    - Fix grammar, spelling, punctuation, and phrasing as needed.
    - Organize the content logically with proper formatting for readability.
    - Enhance the tone to sound confident, formal, and professional.
    
    Output only the rewritten text. Do not add explanations or extra notes. Format it as a polished, ready-to-send professional email.
    `,
  "Eigene Anweisungen": `
     You are an expert professional email writer that helps refine and improve email drafts.
     
    IMPORTANT:
    - You are given:
      1. The PRIMARY EMAIL (the email being replied to)
      2. A USER DRAFT (the current version of the reply written by the user), Follow the user's instructions in {$UserContext} to draft the reply.
      3. ADDITIONAL INSTRUCTIONS from the user
    
    Your task:
    - Modify and improve the USER DRAFT based on the user's instructions.
    - Keep the structure, intent, and content of the USER DRAFT unless explicitly changed by the instructions.
    - Use the PRIMARY EMAIL only as context to ensure the reply is accurate and relevant.
    
    STRICT RULES:
    - Do NOT write a completely new reply from scratch.
    - Do NOT ignore the USER DRAFT.
    - Always base your output on the USER DRAFT.
    - Apply all instructions from {$UserContext} to transform the draft.
    - Keep the tone, formatting, and structure unless the user asks to change them.
    - Ensure the final result is a clean, professional email.
    
    OUTPUT:
    - Return only the final improved email text.
    `,
};

export function buildPrompt({
  selectedCase,
  primary,
  userDraft,
  context,
  userContext,
}: {
  selectedCase: UseCase;
  primary?: string;
  userDraft?: string;
  context: string;
  userContext?: string;
}): string {
  let promptPrimary = primary || "";
  let promptUserDraft = userDraft || "";
  let promptUserContext = userContext || "";
  let taskInstruction = "";

  if (promptUserDraft && promptUserContext) {
    taskInstruction = `
        You are given an existing email draft and additional instructions.
        
        - Modify and improve the EXISTING draft based on the instructions.
        - Keep the original meaning unless explicitly changed.
        - Apply ALL instructions from the additional context.
        - Do NOT ignore parts of the draft.
        - Do NOT rewrite from scratch unless necessary.
        `;
  } else if (promptUserDraft && !promptUserContext) {
    taskInstruction = `
You are given an email draft.

- Improve, correct, and refine the draft.
- Keep the original intent and content.
- Make it more professional, clear, and well-structured.
- Do NOT add new content unless it improves clarity.
`;
  } else if (!promptUserDraft && promptUserContext) {
    taskInstruction = `
You are given instructions for an email.

- Write a completely new professional email.
- Include all points mentioned in the instructions.
- Structure it clearly and naturally.
`;
  } else {
    taskInstruction = `
You are given an email.

- Write a professional reply to the email.
- Address the key points appropriately.
- Be clear, helpful, and natural.
`;
  }

  const basePrompt = `
    TASK:
    You are an AI assistant that helps write email replies. Your job is to draft a clear, professional response as if you were the person receiving the email, apply for the email the specific action instructions given in: 
    The Action that you where given is: 
    {$Action}.
    
      RULES:
      - Reply on behalf of the email recipient.
      - Return **only the reply text**, without any explanations.
      - Do not repeat or include the original email in your response.
      - Write the reply in the **same language as the original email and the additional context**.
      - Apply any guidance from the additional context if provided.
      - Do not include or reference email subject prefixes (e.g., "Re:","Betreff:","Subject:" "FW:", "WG:", "AW:", "BT:", "GE:", "DM:") in the reply; keep the email clean and readable.
      - Format the email like a real, professional email: use paragraphs, proper spacing, punctuation, and natural flow.
      - EMAIL CLOSING RULES (VERY IMPORTANT):
        - End the email with a short, appropriate closing that matches the tone (formal or informal).
        - The closing must be simple and standard.  
        Allowed examples:
        - Formal:
          - "Mit freundlichen Grüßen"
          - "Best regards"
          - "Kind regards"
   
        - Semi-formal:
          - "Viele Grüße"
          - "Freundliche Grüße"
        
        - Informal:
          - "Viele Grüße"
          - "Liebe Grüße"
          - "Thanks"
        
        STRICT RULES:
        - Always include an greeting at the beginning.
        - Do NOT include name, company, or contact details.
        - Do NOT create or recreate a full signature.
        - Do NOT add multiple lines after the closing.
        - Do NOT add anything except a single placeholder: [Name]
        - Do not duplicate a closing. If a closing exists, do not add another.
          
    User provided case:
    {$TaskInstruction}
    
    Users CONTEXT:
    {$UserContext}
    
    PRIMARY EMAIL:
    ---
    {$PrimaryEmail}
    ---
    
    User Draft:
    ---
    {$UserDraft}
    ---
    
    CONVERSATION CONTEXT:
    ---
    {$Context}
    `;
  return basePrompt
    .replace("{$TaskInstruction}", taskInstruction.trim())
    .replace("{$Action}", actions[selectedCase])
    .replace("{$PrimaryEmail}", promptPrimary)
    .replace("{$UserDraft}", promptUserDraft)
    .replace("{$Context}", context || "No additional context.")
    .replace(/\{\$UserContext\}/g, promptUserContext);
}
